import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  deleteReadNotifications,
  getNotificationCompanyFacets,
  getNotificationTemplate,
  getNotificationTemplates,
  getNotifications,
  getUnreadCount,
  markAllAsRead,
  markAsRead,
  updateNotificationTemplate,
} from '@/api/services/notifications.service'
import { getErrorMessage } from '@/lib/getErrorMessage'
import type {
  NotificationQueryParams,
  NotificationTemplateUpdatePayload,
} from '@/types'
import type { MarkNotificationsReadFilter } from '@/api/services/notifications.service'
import { notificationsQueryKeys } from '../notificationQueryKeys'

export { notificationsQueryKeys }

const NOTIFICATIONS_POLL_INTERVAL_MS = 15 * 1000

export function useNotifications(params: NotificationQueryParams | undefined) {
  return useQuery({
    queryKey: notificationsQueryKeys.list(params),
    queryFn: () => getNotifications(params),
    staleTime: NOTIFICATIONS_POLL_INTERVAL_MS,
    gcTime: 5 * 60 * 1000,
    refetchInterval: NOTIFICATIONS_POLL_INTERVAL_MS,
  })
}

/**
 * Infinite (scroll-to-load) variant of {@link useNotifications}. `params.page`
 * is ignored — paging is driven by `getNextPageParam`, which advances until the
 * accumulated item count reaches the server `total`.
 */
export function useInfiniteNotifications(
  params: Omit<NotificationQueryParams, 'page'> | undefined
) {
  return useInfiniteQuery({
    queryKey: notificationsQueryKeys.infiniteList(params),
    queryFn: ({ pageParam }) => getNotifications({ ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.data.length, 0)
      return loaded < lastPage.total ? allPages.length + 1 : undefined
    },
    staleTime: NOTIFICATIONS_POLL_INTERVAL_MS,
    gcTime: 5 * 60 * 1000,
    refetchInterval: NOTIFICATIONS_POLL_INTERVAL_MS,
  })
}

export function useUnreadCount() {
  return useQuery({
    queryKey: notificationsQueryKeys.unreadCount,
    queryFn: getUnreadCount,
    staleTime: NOTIFICATIONS_POLL_INTERVAL_MS,
    gcTime: 5 * 60 * 1000,
    refetchInterval: NOTIFICATIONS_POLL_INTERVAL_MS,
  })
}

export function useNotificationCompanyFacets() {
  return useQuery({
    queryKey: notificationsQueryKeys.companyFacets,
    queryFn: getNotificationCompanyFacets,
    staleTime: NOTIFICATIONS_POLL_INTERVAL_MS,
    gcTime: 5 * 60 * 1000,
    refetchInterval: NOTIFICATIONS_POLL_INTERVAL_MS,
  })
}

export function useNotificationTemplates() {
  return useQuery({
    queryKey: notificationsQueryKeys.templates,
    queryFn: getNotificationTemplates,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export function useNotificationTemplate(id: string | null) {
  return useQuery({
    queryKey: notificationsQueryKeys.template(id ?? ''),
    queryFn: () => getNotificationTemplate(id!),
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export function useMarkAsRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id }: { id: string; silent?: boolean }) => markAsRead(id),
    onMutate: ({ id }) => {
      const updatePage = (value: unknown): unknown => {
        if (!value || typeof value !== 'object') return value
        if ('pages' in value && Array.isArray(value.pages)) {
          return { ...value, pages: value.pages.map(updatePage) }
        }
        if ('data' in value && Array.isArray(value.data)) {
          return {
            ...value,
            data: value.data.map((item: { _id?: string; isRead?: boolean }) =>
              item?._id === id ? { ...item, isRead: true } : item
            ),
          }
        }
        return value
      }
      queryClient.setQueriesData({ queryKey: notificationsQueryKeys.lists }, updatePage)
      queryClient.setQueriesData({ queryKey: notificationsQueryKeys.infiniteLists }, updatePage)
      queryClient.setQueryData<number>(notificationsQueryKeys.unreadCount, (count) =>
        Math.max(0, (count ?? 0) - 1)
      )
    },
    onSuccess: (_, variables) => {
      if (!variables.silent) toast.success('Marked as read')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: notificationsQueryKeys.lists })
      queryClient.invalidateQueries({ queryKey: notificationsQueryKeys.infiniteLists })
      queryClient.invalidateQueries({ queryKey: notificationsQueryKeys.unreadCount })
      queryClient.invalidateQueries({ queryKey: notificationsQueryKeys.companyFacets })
    },
  })
}

export function useMarkAllAsRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (filter: MarkNotificationsReadFilter) => markAllAsRead(filter),
    onMutate: (filter) => {
      if (Object.keys(filter).length === 0) {
        queryClient.setQueryData<number>(notificationsQueryKeys.unreadCount, 0)
      }
    },
    onSuccess: () => toast.success('Notifications marked as read'),
    onError: (error) => toast.error(getErrorMessage(error)),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: notificationsQueryKeys.lists })
      queryClient.invalidateQueries({ queryKey: notificationsQueryKeys.infiniteLists })
      queryClient.invalidateQueries({ queryKey: notificationsQueryKeys.unreadCount })
      queryClient.invalidateQueries({ queryKey: notificationsQueryKeys.companyFacets })
    },
  })
}

export function useDeleteReadNotifications() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteReadNotifications,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationsQueryKeys.all })
      toast.success('Read notifications cleared')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useUpdateNotificationTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string
      payload: NotificationTemplateUpdatePayload
    }) => updateNotificationTemplate(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: notificationsQueryKeys.templates })
      queryClient.invalidateQueries({
        queryKey: notificationsQueryKeys.template(variables.id),
      })
      toast.success('Template updated')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}
